import { AssessmentSelectDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { formatAssessmentDate } from '@utils/assessment.util';
import { FC, useMemo } from 'react';

type TrlSummaryStatsProps = {
  assessments: AssessmentSelectDto[];
  isLoading?: boolean;
  onNavigateToAssessment: (assessmentId: string) => void;
};

type StatItemProps = {
  isClickable?: boolean;
  label: string;
  onClick?: () => void;
  value: string;
};

const StatItem: FC<StatItemProps> = (props) => {
  const { isClickable, label, onClick, value } = props;

  if (isClickable && onClick) {
    return (
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{label}</span>
        <button
          className="cursor-pointer font-medium text-primary-600 hover:underline"
          onClick={onClick}
          type="button"
        >
          {value}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
};

export const TrlSummaryStats: FC<TrlSummaryStatsProps> = (props) => {
  const { assessments, isLoading, onNavigateToAssessment } = props;

  const stats = useMemo(() => {
    if (assessments.length === 0) {
      return null;
    }

    const scores = assessments.map((a) => a.score);
    const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;

    const highestAssessment = assessments.reduce((prev, curr) => (curr.score > prev.score ? curr : prev));

    const lowestAssessment = assessments.reduce((prev, curr) => (curr.score < prev.score ? curr : prev));

    const sortedByDate = [...assessments].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const latestAssessment = sortedByDate[0];

    return {
      avg: avg.toFixed(1),
      highest: {
        assessmentId: highestAssessment.id,
        domain: highestAssessment.domain?.name ?? 'N/A',
        score: highestAssessment.score,
      },
      latest: latestAssessment ? formatAssessmentDate(latestAssessment) : 'N/A',
      lowest: {
        assessmentId: lowestAssessment.id,
        domain: lowestAssessment.domain?.name ?? 'N/A',
        score: lowestAssessment.score,
      },
      total: assessments.length,
    };
  }, [assessments]);

  return (
    <Tile
      emptyState={{ title: 'No assessments yet' }}
      header={{ title: 'TRL Summary' }}
      isEmpty={!stats}
      isLoading={isLoading}
    >
      {stats && (
        <div className="flex flex-col gap-2">
          <StatItem
            label="Average"
            value={stats.avg}
          />
          <StatItem
            isClickable
            label="Highest"
            onClick={() => onNavigateToAssessment(stats.highest.assessmentId)}
            value={`${stats.highest.score} (${stats.highest.domain})`}
          />
          <StatItem
            isClickable
            label="Lowest"
            onClick={() => onNavigateToAssessment(stats.lowest.assessmentId)}
            value={`${stats.lowest.score} (${stats.lowest.domain})`}
          />
          <StatItem
            label="Total Assessments"
            value={String(stats.total)}
          />
          <StatItem
            label="Latest Assessment"
            value={stats.latest}
          />
        </div>
      )}
    </Tile>
  );
};
